import { FormContent } from "@/components/page-canvas";
import { Form, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Choice } from "../../requests/controls";
import { formatLocationTime } from "@/lib/location-time";

export type InstagramSetupProps = {
  available: boolean;
  environment: string;
  locations: { id: string; name: string }[];
  authorization: {
    id: string;
    location_name: string;
    accounts: { id: string; name: string }[];
  } | null;
  accounts: {
    id: string;
    name: string;
    location_id: string;
    ready: boolean;
    state: string;
    verified_at: string | null;
  }[];
};

export function InstagramSetup({ setup }: { setup: InstagramSetupProps }) {
  const { t } = useTranslation();
  const { i18n } = usePage().props;
  return (
    <Card data-layout-frame="">
      <CardHeader>
        <CardDescription>{t("instagram.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <FormContent className="space-y-4">
          <Badge variant="outline">
            {t(
              `instagram.environment.${setup.environment === "live" ? "live" : "test"}`,
            )}
          </Badge>
          <p className="text-muted-foreground text-sm">
            {t("instagram.constraints")}
          </p>
          <a
            className="inline-block text-sm underline underline-offset-4"
            href="https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/"
            target="_blank"
            rel="noreferrer"
          >
            {t("instagram.guide")}
          </a>
          {!setup.available && (
            <p className="text-muted-foreground text-sm">
              {t("instagram.unavailable")}
            </p>
          )}
          {setup.available && setup.locations.length > 0 && (
            <Form
              transform={(data) => ({ ...data, return_section: "setup" })}
              action="/user/instagram_authorization"
              method="post"
            >
              {({ processing }) => (
                <div className="space-y-3">
                  <Choice
                    id="instagram-intake-location"
                    name="instagram_authorization[location_id]"
                    label={t("instagram.default_location")}
                    options={setup.locations}
                    defaultValue={setup.locations[0].id}
                  />
                  <Button type="submit" disabled={processing}>
                    {t("instagram.authorize")}
                  </Button>
                </div>
              )}
            </Form>
          )}
          {setup.available && setup.authorization && (
            <section
              className="space-y-3 rounded-md border p-3"
              aria-label={t("instagram.choose_account")}
            >
              <h3 className="font-medium">{t("instagram.choose_account")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("instagram.intake_at", {
                  location: setup.authorization.location_name,
                })}
              </p>
              {setup.authorization.accounts.length === 0 ? (
                <p>{t("instagram.no_accounts")}</p>
              ) : (
                <Form
                  transform={(data) => ({ ...data, return_section: "setup" })}
                  action="/user/instagram_accounts"
                  method="post"
                >
                  {({ processing }) => (
                    <div className="space-y-3">
                      <input
                        type="hidden"
                        name="instagram_account[authorization_id]"
                        value={setup.authorization!.id}
                      />
                      <Choice
                        id="instagram-account"
                        name="instagram_account[professional_id]"
                        label={t("instagram.account")}
                        options={setup.authorization!.accounts}
                        defaultValue={setup.authorization!.accounts[0].id}
                      />
                      <Button type="submit" disabled={processing}>
                        {t("instagram.connect")}
                      </Button>
                    </div>
                  )}
                </Form>
              )}
            </section>
          )}
          {setup.accounts.map((profile) => (
            <section
              className="space-y-3 rounded-md border p-3"
              key={profile.id}
              aria-label={profile.name}
            >
              <h3 className="font-medium break-words">{profile.name}</h3>
              <p className="text-sm">
                {t(
                  `instagram.states.${profile.ready ? "ready" : profile.state === "pending" ? "pending" : profile.state === "disconnected" ? "disconnected" : "unavailable"}`,
                )}
              </p>
              {profile.verified_at && (
                <p className="text-muted-foreground text-sm">
                  {t("instagram.checked", {
                    date: formatLocationTime(profile.verified_at, i18n, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }),
                  })}
                </p>
              )}
              <Form
                transform={(data) => ({ ...data, return_section: "setup" })}
                action={`/user/instagram_accounts/${profile.id}`}
                method="patch"
              >
                {({ processing }) => (
                  <div className="space-y-3">
                    <Choice
                      id={`instagram-location-${profile.id}`}
                      name="instagram_account[location_id]"
                      label={t("instagram.default_location")}
                      options={setup.locations}
                      defaultValue={profile.location_id}
                    />
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={processing}
                    >
                      {t("instagram.save_location")}
                    </Button>
                  </div>
                )}
              </Form>
              {profile.state !== "disconnected" && (
                <Form
                  transform={(data) => ({ ...data, return_section: "setup" })}
                  action={`/user/instagram_accounts/${profile.id}`}
                  method="delete"
                >
                  {({ processing }) => (
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={processing}
                    >
                      {t("instagram.disconnect")}
                    </Button>
                  )}
                </Form>
              )}
            </section>
          ))}
        </FormContent>
      </CardContent>
    </Card>
  );
}
